import React, { useState, useEffect, useRef, useMemo } from 'react';
import { invoicesService } from '../services/invoices.service';
import { providersService, Provider } from '../services/providers.service';
import { filesService } from '../services/files.service';
import '../styles/AddInvoiceForm.css';
import { getErrorMessage } from '../utils/error';

interface AddInvoiceFormProps {
  colocId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const AddInvoiceForm: React.FC<AddInvoiceFormProps> = ({ colocId, onSuccess, onCancel }) => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [providerId, setProviderId] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [initialAmount, setInitialAmount] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [periodEndDate, setPeriodEndDate] = useState('');
  // Ce que la facture a fait consommer. Optionnel : toutes les factures d'énergie
  // n'en portent pas — celles d'été, à abonnement seul, n'ont aucune quantité.
  const [consumptionQuantity, setConsumptionQuantity] = useState('');
  const [consumptionUnit, setConsumptionUnit] = useState('kWh');
  // Relevé ou estimation (ticket #14) ; « » = non précisé.
  const [consumptionEstimated, setConsumptionEstimated] = useState('');

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedProvider = useMemo(
    () => providers.find((p) => p.id === providerId),
    [providers, providerId]
  );

  // Load providers for the coloc
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const data = await providersService.getProvidersByColocId(colocId);
        setProviders(data);
        if (data.length > 0) {
          setProviderId(data[0].id);
        }
      } catch (err) {
        setError('Échec du chargement des fournisseurs');
      }
    };

    fetchProviders();
  }, [colocId]);

  // Generate preview URL when file is selected
  useEffect(() => {
    if (!selectedFile) {
      setFilePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    setFileError(null);

    if (!files || files.length === 0) {
      setSelectedFile(null);
      return;
    }

    const file = files[0];

    // Check if file is a PDF or image
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setFileError('Seuls les fichiers PDF, JPG et PNG sont acceptés');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!providerId || !invoiceDate || !initialAmount || !invoiceAmount) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if ((periodStartDate && !periodEndDate) || (!periodStartDate && periodEndDate)) {
      setError('Les dates de début et fin de période doivent être saisies ensemble');
      return;
    }

    if (!selectedFile) {
      setFileError('Veuillez sélectionner un fichier (PDF, JPG ou PNG)');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Extract year from invoice date
      const invoiceYear = invoiceDate ? new Date(invoiceDate).getFullYear() : new Date().getFullYear();

      // First upload the file with provider ID and year for proper folder structure
      const uploadResult = await filesService.uploadFile(selectedFile, colocId, providerId, invoiceYear);

      if (!uploadResult.success) {
        throw new Error(uploadResult.message || 'Échec du téléchargement du fichier');
      }

      // Format the invoice data with the uploaded filename
      const invoiceData: Record<string, any> = {
        fileName: uploadResult.filename,
        providerId,
        colocId,
        invoiceDate: new Date(invoiceDate).toISOString(),
        initialAmount: parseFloat(initialAmount),
        invoiceAmount: parseFloat(invoiceAmount),
        isValidated: true,
        dateValidated: new Date().toISOString(),
      };

      if (periodStartDate) invoiceData.periodStartDate = new Date(periodStartDate).toISOString();
      if (periodEndDate) invoiceData.periodEndDate = new Date(periodEndDate).toISOString();
      if (consumptionQuantity !== '') {
        invoiceData.consumptionQuantity = parseFloat(consumptionQuantity);
        invoiceData.consumptionUnit = consumptionUnit;
        if (consumptionEstimated !== '') invoiceData.consumptionEstimated = consumptionEstimated === 'true';
      }

      // Create the invoice
      await invoicesService.createInvoice(invoiceData);

      // Notify parent component of success
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err, 'Échec de la création de la facture'));
    } finally {
      setLoading(false);
    }
  };

  const isImage = selectedFile?.type.startsWith('image/');
  const isPdf = selectedFile?.type === 'application/pdf';

  return (
    <div className={`add-invoice-form-container ${selectedFile ? 'with-preview' : ''}`}>
      <div className="add-invoice-form-header">
        <h3>Ajouter une facture manuellement</h3>
        <button className="close-button" onClick={onCancel}>×</button>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="add-invoice-form-body">
        <div className="add-invoice-form-scroll">
          <form onSubmit={handleSubmit} className="add-invoice-form">
          <div className="form-group">
            <label htmlFor="file">Fichier (PDF, JPG, PNG) *</label>
            <input
              type="file"
              id="file"
              ref={fileInputRef}
              accept="application/pdf,image/jpeg,image/png"
              onChange={handleFileChange}
              required
            />
            {fileError && <div className="file-error">{fileError}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="providerId">Fournisseur *</label>
            <select
              id="providerId"
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
              required
            >
              {providers.length === 0 && <option value="">Aucun fournisseur disponible</option>}
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.providerName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="invoiceDate">Date de facture *</label>
            <input
              type="date"
              id="invoiceDate"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="initialAmount">Montant initial (€) *</label>
            <input
              type="number"
              id="initialAmount"
              value={initialAmount}
              onChange={(e) => setInitialAmount(e.target.value)}
              step="0.01"
              min="0"
              placeholder="0.00"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="invoiceAmount">Montant final (€) *</label>
            <input
              type="number"
              id="invoiceAmount"
              value={invoiceAmount}
              onChange={(e) => setInvoiceAmount(e.target.value)}
              step="0.01"
              min="0"
              placeholder="0.00"
              required
            />
          </div>

          {selectedProvider?.requiresConsumptionPeriod && (
            <div className="form-group">
              <label>Période de consommation (optionnel)</label>
              <div className="period-fields">
                <input
                  type="date"
                  id="periodStartDate"
                  value={periodStartDate}
                  onChange={(e) => setPeriodStartDate(e.target.value)}
                  placeholder="Début"
                />
                <span className="period-separator">→</span>
                <input
                  type="date"
                  id="periodEndDate"
                  value={periodEndDate}
                  onChange={(e) => setPeriodEndDate(e.target.value)}
                  placeholder="Fin"
                />
                {(periodStartDate || periodEndDate) && (
                  <button type="button" className="clear-date-button" onClick={() => { setPeriodStartDate(""); setPeriodEndDate(""); }} title="Effacer la période">×</button>
                )}
              </div>
            </div>
          )}

          {selectedProvider?.requiresConsumptionPeriod && (
            <div className="form-group">
              <label htmlFor="consumptionQuantity">Consommation (optionnel)</label>
              <div className="consumption-fields">
                <input
                  type="number"
                  id="consumptionQuantity"
                  value={consumptionQuantity}
                  onChange={(e) => setConsumptionQuantity(e.target.value)}
                  step="0.001"
                  min="0"
                  placeholder="Quantité"
                />
                <select
                  id="consumptionUnit"
                  value={consumptionUnit}
                  onChange={(e) => setConsumptionUnit(e.target.value)}
                  aria-label="Unité de consommation"
                >
                  <option value="kWh">kWh</option>
                  <option value="m3">m³</option>
                  <option value="MWh">MWh</option>
                </select>
                <select
                  id="consumptionEstimated"
                  value={consumptionEstimated}
                  onChange={(e) => setConsumptionEstimated(e.target.value)}
                  aria-label="Nature du relevé"
                >
                  <option value="">Relevé ou estimation ?</option>
                  <option value="false">Relevé</option>
                  <option value="true">Estimation</option>
                </select>
              </div>
              <small className="field-hint">
                Ce que la facture a fait consommer. Sans elle, une hausse de prix
                ne se distingue pas d'une hausse d'usage.
              </small>
            </div>
          )}

          </form>
        </div>

        {selectedFile && filePreviewUrl && (
          <div className="add-invoice-preview">
            <h4>Aperçu</h4>
            {isPdf ? (
              <iframe
                src={filePreviewUrl}
                className="preview-iframe"
                title="Aperçu PDF"
              />
            ) : isImage ? (
              <img
                src={filePreviewUrl}
                alt={selectedFile.name}
                className="preview-image"
              />
            ) : null}
          </div>
        )}
      </div>

      <div className="form-actions">
        <button type="button" className="cancel-button" onClick={onCancel} disabled={loading}>
          Annuler
        </button>
        <button
          type="button"
          className="submit-button"
          disabled={loading}
          onClick={(e) => {
            e.preventDefault();
            const form = document.querySelector('.add-invoice-form') as HTMLFormElement;
            if (form) form.requestSubmit();
          }}
        >
          {loading ? 'Création en cours...' : 'Créer la facture'}
        </button>
      </div>
    </div>
  );
};

export default AddInvoiceForm;
